import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEdit,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';

const formatRupee = (value) => {
  const val = Number(value) || 0;
  return `₹${val.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: val % 1 === 0 ? 0 : 2,
  })}`;
};

const AdsPlatformSummaryTable = ({
  platforms = [],
  loading = false,
  onPlatformClick,
  groupBy = 'platform',
  onCampaignUpdated,
}) => {
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  const isCampaignView = groupBy === 'campaign';

  const [searchPlatform, setSearchPlatform] = useState('');
  const [searchSheetCampaign, setSearchSheetCampaign] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    cost: '',
    leads: '',
    cpl: '',
    likely: '',
    sales: '',
    revenue: '',
    cps: '',
    roas: '',
    records: '',
  });
  const [sortCol, setSortCol] = useState('cost');
  const [sortDir, setSortDir] = useState('DESC');

  // Edit Campaign Name Modal State
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const handleOpenEditModal = (row) => {
    setEditingCampaign(row);
    setNewCampaignName(row.campaign_name || row.campaign || row.name || '');
  };

  const handleCloseEditModal = () => {
    if (!savingName) {
      setEditingCampaign(null);
      setNewCampaignName('');
    }
  };

  const handleSaveCampaignName = async (e) => {
    if (e) e.preventDefault();
    const rawCampaign = editingCampaign?.campaign || editingCampaign?.name;
    const cleanName = newCampaignName.trim();

    if (!rawCampaign) {
      showToast.error('Campaign is missing.');
      return;
    }
    if (!cleanName) {
      showToast.error('Please enter a valid campaign name.');
      return;
    }

    try {
      setSavingName(true);
      const token = localStorage.getItem('authToken');
      const res = await axios.put(
        `${API_URL}report-ads/update-campaign-name`,
        {
          campaign: rawCampaign,
          campaign_name: cleanName,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (res.data?.success) {
        showToast.success(res.data.message || 'Campaign name updated successfully for all matching records!');
        setEditingCampaign(null);
        setNewCampaignName('');
        if (onCampaignUpdated) {
          onCampaignUpdated();
        }
      } else {
        showToast.error(res.data?.message || 'Failed to update campaign name.');
      }
    } catch (err) {
      console.error('Update campaign name error:', err);
      showToast.error(err.response?.data?.message || 'Failed to update campaign name.');
    } finally {
      setSavingName(false);
    }
  };

  // Double scroll sync
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [platforms]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleSort = (columnKey) => {
    if (sortCol === columnKey) {
      setSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortCol(columnKey);
      setSortDir('DESC');
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortCol !== columnKey) return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.35, marginLeft: 5 }} />;
    return sortDir === 'ASC' ? (
      <FontAwesomeIcon icon={faSortUp} style={{ color: '#007bff', marginLeft: 5 }} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} style={{ color: '#007bff', marginLeft: 5 }} />
    );
  };

  const handleFilterChange = (field, val) => {
    setColumnFilters((prev) => ({ ...prev, [field]: val }));
  };

  // Filter & Sort platforms
  const filteredPlatforms = platforms
    .filter((p) => {
      if (isCampaignView) {
        const sheetCamp = p.campaign || '';
        if (
          searchSheetCampaign.trim() &&
          !sheetCamp.toLowerCase().includes(searchSheetCampaign.toLowerCase().trim())
        ) {
          return false;
        }
        const crmCamp = p.campaign_name || p.name || '';
        if (
          searchPlatform.trim() &&
          !crmCamp.toLowerCase().includes(searchPlatform.toLowerCase().trim())
        ) {
          return false;
        }
      } else {
        const platformName = p.platform || p.name || '';
        if (
          searchPlatform.trim() &&
          !platformName.toLowerCase().includes(searchPlatform.toLowerCase().trim())
        ) {
          return false;
        }
      }
      if (columnFilters.cost !== '' && Number(p.cost || 0) < Number(columnFilters.cost)) return false;
      if (columnFilters.leads !== '' && Number(p.leads || 0) < Number(columnFilters.leads)) return false;
      if (columnFilters.cpl !== '' && Number(p.cpl || 0) < Number(columnFilters.cpl)) return false;
      if (columnFilters.likely !== '' && Number(p.likely || 0) < Number(columnFilters.likely)) return false;
      if (columnFilters.sales !== '' && Number(p.sales || 0) < Number(columnFilters.sales)) return false;
      if (columnFilters.revenue !== '' && Number(p.revenue || 0) < Number(columnFilters.revenue)) return false;
      if (columnFilters.cps !== '' && Number(p.cps || 0) < Number(columnFilters.cps)) return false;
      if (columnFilters.roas !== '' && Number(p.roas || 0) < Number(columnFilters.roas)) return false;
      if (columnFilters.records !== '' && Number(p.records || 0) < Number(columnFilters.records)) return false;
      return true;
    })
    .sort((a, b) => {
      let valA = a[sortCol];
      let valB = b[sortCol];

      if (typeof valA === 'string') {
        const res = valA.localeCompare(valB || '');
        return sortDir === 'ASC' ? res : -res;
      }

      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
      return sortDir === 'ASC' ? valA - valB : valB - valA;
    });

  return (
    <div className="panel panel-default with-rounded-corner with-shadow" style={{ marginBottom: 0 }}>
      {/* Double Scroll Container */}
      <div className="large-table-container-3">
        {/* Top Scrollbar */}
        <div
          ref={topScrollbarRef}
          onScroll={handleTopScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '14px',
            marginBottom: '1px',
            background: '#f8f9fa',
          }}
        >
          <div ref={topScrollbarContentRef} style={{ height: '1px' }}></div>
        </div>

        {/* Table Container */}
        <div
          ref={tableContainerRef}
          onScroll={syncScroll}
          style={{
            overflowY: 'auto',
            height: 'calc(112vh - 300px)',
            minHeight: '340px',
            overflowX: 'auto',
            position: 'relative',
          }}
        >
          <table className="table table-condensed table-hover bg-white sticky-table">
            <thead>
              {/* Header Sort Row */}
              <tr style={{ cursor: 'pointer' }}>
                {isCampaignView ? (
                  <>
                    <th style={{ minWidth: '170px' }} onClick={() => handleSort('campaign')}>
                      Sheet Campaign {getSortIcon('campaign')}
                    </th>
                    <th style={{ minWidth: '170px' }} onClick={() => handleSort('campaign_name')}>
                      CRM Campaign Name {getSortIcon('campaign_name')}
                    </th>
                  </>
                ) : (
                  <th style={{ minWidth: '160px' }} onClick={() => handleSort('platform')}>
                    Platform {getSortIcon('platform')}
                  </th>
                )}
                <th style={{ textAlign: 'right', minWidth: '120px' }} onClick={() => handleSort('cost')}>
                  Total Spend {getSortIcon('cost')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('leads')}>
                  Total Leads {getSortIcon('leads')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('cpl')}>
                  Cost / Lead (CPL) {getSortIcon('cpl')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('likely')}>
                  Likely Leads {getSortIcon('likely')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('sales')}>
                  Total Sales {getSortIcon('sales')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '130px' }} onClick={() => handleSort('revenue')}>
                  Total Revenue {getSortIcon('revenue')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '110px' }} onClick={() => handleSort('cps')}>
                  Cost / Sale (CPS) {getSortIcon('cps')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('roas')}>
                  ROAS {getSortIcon('roas')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '100px' }} onClick={() => handleSort('records')}>
                  Ad Records {getSortIcon('records')}
                </th>
                <th style={{ textAlign: 'center', minWidth: '70px', width: '70px' }}>Action</th>
              </tr>

              {/* Inline Filter Row */}
              <tr>
                {isCampaignView ? (
                  <>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search Sheet Campaign..."
                        style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px' }}
                        value={searchSheetCampaign}
                        onChange={(e) => setSearchSheetCampaign(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search CRM Campaign..."
                        style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px' }}
                        value={searchPlatform}
                        onChange={(e) => setSearchPlatform(e.target.value)}
                      />
                    </th>
                  </>
                ) : (
                  <th>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Search platform..."
                      style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px' }}
                      value={searchPlatform}
                      onChange={(e) => setSearchPlatform(e.target.value)}
                    />
                  </th>
                )}
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Spend..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.cost}
                    onChange={(e) => handleFilterChange('cost', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Leads..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.leads}
                    onChange={(e) => handleFilterChange('leads', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min CPL..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.cpl}
                    onChange={(e) => handleFilterChange('cpl', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Likely..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.likely}
                    onChange={(e) => handleFilterChange('likely', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Sales..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.sales}
                    onChange={(e) => handleFilterChange('sales', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Revenue..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.revenue}
                    onChange={(e) => handleFilterChange('revenue', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min CPS..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.cps}
                    onChange={(e) => handleFilterChange('cps', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min ROAS..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.roas}
                    onChange={(e) => handleFilterChange('roas', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Records..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.records}
                    onChange={(e) => handleFilterChange('records', e.target.value)}
                  />
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, rIdx) => (
                  <tr key={`skeleton-${rIdx}`}>
                    <td><div className="skeleton-text" style={{ width: '100px', height: '14px' }}></div></td>
                    {isCampaignView && <td><div className="skeleton-text" style={{ width: '100px', height: '14px' }}></div></td>}
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '80px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '50px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '60px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '50px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '40px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '80px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '60px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '50px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '40px', height: '14px', float: 'right' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '30px', height: '14px', margin: '0 auto' }}></div></td>
                  </tr>
                ))
              ) : filteredPlatforms.length === 0 ? (
                <tr>
                  <td colSpan={isCampaignView ? 12 : 11} className="text-center text-muted" style={{ padding: '30px 15px' }}>
                    No {isCampaignView ? 'campaign mappings' : 'ad platforms'} found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredPlatforms.map((row, idx) => {
                  const targetName = isCampaignView
                    ? row.campaign_name || row.campaign || 'Unmapped'
                    : row.platform || 'Unmapped';
                  return (
                    <tr
                      key={idx}
                      onClick={() => onPlatformClick && onPlatformClick(targetName)}
                      style={{ cursor: 'pointer' }}
                      title={`Click to view ${targetName} details`}
                    >
                      {isCampaignView ? (
                        <>
                          <td style={{ color: '#334155', fontWeight: 500 }}>
                            {row.campaign || '-'}
                          </td>
                          <td style={{ fontWeight: 600, color: '#007bff' }}>
                            {row.campaign_name || row.campaign || 'Unmapped'}
                          </td>
                        </>
                      ) : (
                        <td style={{ fontWeight: 600, color: '#007bff' }}>
                          {row.platform}
                        </td>
                      )}
                      <td style={{ textAlign: 'right' }}>{formatRupee(row.cost)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.leads || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.cpl > 0 ? formatRupee(row.cpl) : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.likely || 0).toLocaleString('en-IN')}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.sales || 0).toLocaleString('en-IN')}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatRupee(row.revenue)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.cps > 0 ? formatRupee(row.cps) : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.roas || 0}x
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.records || 0).toLocaleString('en-IN')}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {groupBy === 'campaign' ? (
                          <button
                            type="button"
                            className="btn btn-warning btn-xs"
                            title="Edit CRM Campaign Name"
                            onClick={() => handleOpenEditModal(row)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Campaign Name Modal */}
      {editingCampaign && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: '500px' }}>
            <div className="modal-content" style={{ borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <div
                className="modal-header"
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontSize: '15px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-warning" />
                  Update CRM Campaign Name
                </h5>
                <button
                  type="button"
                  className="close"
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}
                  onClick={handleCloseEditModal}
                  disabled={savingName}
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleSaveCampaignName}>
                <div className="modal-body" style={{ padding: '20px' }}>
                  <div className="mb-3">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#495057', display: 'block', marginBottom: '4px' }}>
                      Sheet Campaign (From Google Sheet):
                    </label>
                    <div
                      style={{
                        padding: '8px 12px',
                        background: '#f1f5f9',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1e293b',
                        wordBreak: 'break-word',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {editingCampaign.campaign || editingCampaign.name}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Platform:</label>
                      <div style={{ fontSize: '12.5px', fontWeight: 500 }}>{editingCampaign.platform || 'Other'}</div>
                    </div>
                    <div className="col-6 text-end">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Matched DB Records:</label>
                      <div>
                        <span className="badge bg-primary" style={{ fontSize: '12px', padding: '4px 8px' }}>
                          {editingCampaign.records || 0} rows
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-2">
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', display: 'block', marginBottom: '4px' }}>
                      CRM Campaign Name <span className="text-danger">*</span>:
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter CRM campaign name..."
                      style={{ fontSize: '13px', padding: '8px 12px' }}
                      value={newCampaignName}
                      onChange={(e) => setNewCampaignName(e.target.value)}
                      autoFocus
                      required
                    />
                    <small className="text-muted" style={{ fontSize: '11px', display: 'block', marginTop: '6px' }}>
                      <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-info" />
                      Updating this will update <strong>CRM Campaign Name</strong> for all {editingCampaign.records || 0} matching records in{' '}
                      <code>tbl_ads_report</code> for this Sheet Campaign.
                    </small>
                  </div>
                </div>

                <div
                  className="modal-footer"
                  style={{
                    padding: '12px 20px',
                    borderTop: '1px solid #e9ecef',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={handleCloseEditModal}
                    disabled={savingName}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={savingName || !newCampaignName.trim()}
                  >
                    {savingName ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdsPlatformSummaryTable;
